import { collection, getDocs, query, where } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaInstagram, FaTiktok, FaFacebook, FaYoutube } from "react-icons/fa";
import appStoreBadge from "../assets/download-apple.png";
import playStoreBadge from "../assets/download-google.png";
import everLogo from "../assets/ever.png";
import { db } from "../firebaseConfig";
import "./ServiceLanding.css";

function ServiceLanding() {
  const { t } = useTranslation();
  const { serviceId, shortId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // shortId = new format (/s/name/shortId), serviceId = legacy format (/s/userId)
  const lookupId = shortId || serviceId;
  const isShortId = !!shortId;

  // Only allow alphanumeric + hyphens/underscores (Firebase UID format)
  const isValidId = /^[a-zA-Z0-9_-]{6,128}$/.test(lookupId || '');

  useEffect(() => {
    if (!isValidId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    async function fetchService() {
      try {
        let q;
        if (isShortId) {
          // Prefix query: find userId that starts with shortId
          q = query(
            collection(db, "UserPost"),
            where("userId", ">=", lookupId),
            where("userId", "<=", lookupId + "\uf8ff")
          );
        } else {
          q = query(
            collection(db, "UserPost"),
            where("userId", "==", lookupId)
          );
        }
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          setNotFound(true);
        } else {
          setService(snapshot.docs[0].data());
        }
      } catch (err) {
        console.error("Error fetching service:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    fetchService();
  }, [lookupId, isShortId, isValidId]);

  const handleOpenInApp = () => {
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isAndroid = /android/i.test(navigator.userAgent);

    // Use hidden iframe to silently try the custom scheme — avoids Safari "invalid address" error
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = `eapp://service/${service?.userId || lookupId}`;
    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
      if (isIOS) {
        window.location.href =
          "https://apps.apple.com/es/app/ever/id6737575221";
      } else if (isAndroid) {
        window.location.href =
          "https://play.google.com/store/apps/details?id=ken.expo.eapp";
      }
    }, 2000);
  };

  if (loading) {
    return (
      <div className="sl-container">
        <img src={everLogo} alt="Ever" className="sl-logo" />
        <div className="sl-loading">{t("sl_loading")}</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="sl-container">
        <img src={everLogo} alt="Ever" className="sl-logo" />
        <div className="sl-not-found">
          <h2>{t("sl_not_found_title")}</h2>
          <p>{t("sl_not_found_desc")}</p>
        </div>
        <div className="sl-store-buttons">
          <a
            href="https://apps.apple.com/es/app/ever/id6737575221"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={appStoreBadge} alt="Download on App Store" />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=ken.expo.eapp"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={playStoreBadge} alt="Get it on Google Play" />
          </a>
        </div>
      </div>
    );
  }

  const imageUrl =
    service.postImages?.[0] || service.imageUrl || service.image || null;
  const providerName = service.name || service.userName || "";
  const companyName = service.companyName || "";

  const socials = [
    { enabled: service.instagramEnabled, link: service.instagramLink, icon: <FaInstagram size={28} color="#E1306C" />, key: "instagram" },
    { enabled: service.tiktokEnabled, link: service.tiktokLink, icon: <FaTiktok size={28} color="#fff" />, key: "tiktok" },
    { enabled: service.facebookEnabled, link: service.facebookLink, icon: <FaFacebook size={28} color="#1877F2" />, key: "facebook" },
    { enabled: service.youtubeEnabled, link: service.youtubeLink, icon: <FaYoutube size={28} color="#FF0000" />, key: "youtube" },
  ].filter((s) => s.enabled && s.link);
  const category = service.category || service.serviceType || "";
  const title = service.title || service.serviceName || providerName;
  const rating = service.rating ? Number(service.rating).toFixed(1) : null;

  return (
    <div className="sl-container">
      <img src={everLogo} alt="Ever" className="sl-logo" />

      <div className="sl-card">
        {imageUrl && (
          <img src={imageUrl} alt={title} className="sl-service-image" />
        )}
        <div className="sl-service-info">
          {category ? <span className="sl-category">{category}</span> : null}
          <div className="sl-title-row">
            <h1 className="sl-title">{title}</h1>
            {companyName ? (
              <>
                <span className="sl-title-divider">|</span>
                <span className="sl-company">{companyName}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {socials.length > 0 && (
        <div className="sl-social-row">
          {socials.map((s) => (
            <a
              key={s.key}
              href={s.link.startsWith("http") ? s.link : `https://${s.link}`}
              target="_blank"
              rel="noopener noreferrer"
              className="sl-social-icon"
            >
              {s.icon}
            </a>
          ))}
        </div>
      )}

      <p className="sl-tagline">{t("sl_tagline")}</p>

      <button className="sl-open-btn" onClick={handleOpenInApp}>
        {t("sl_open_btn")}
      </button>

      <p className="sl-download-label">{t("sl_download_label")}</p>
      <div className="sl-store-buttons">
        <a
          href="https://apps.apple.com/es/app/ever/id6737575221"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={appStoreBadge} alt="Download on App Store" />
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=ken.expo.eapp"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={playStoreBadge} alt="Get it on Google Play" />
        </a>
      </div>
    </div>
  );
}

export default ServiceLanding;
